import type { ErrorEvent, EventHint, Log } from '@sentry/angular';
import type { TransactionEvent } from '@sentry/core';

/**
 * User fields considered Personally Identifiable Information.
 * Removed from `event.user` when PII filtering is active.
 */
const PII_USER_FIELDS: readonly (keyof NonNullable<ErrorEvent['user']>)[] = ['email', 'ip_address', 'username'];

/**
 * Request header names considered Personally Identifiable Information.
 * Matched case-insensitively and removed from `event.request.headers`
 * when PII filtering is active.
 */
const PII_HEADER_NAMES = new Set(['authorization', 'cookie', 'x-forwarded-for', 'x-real-ip']);

/** A bare query string: an optional `?`, a parameter name, then `=` (`searchTerm=x`, `?a=1&b=2`). */
const BARE_QUERY = /^\??[\w.[\]-]+=/;

/** An absolute http(s) URL, or a root-relative path at the start of the text or after a space, quote or `(`. */
const URL_TOKEN = /https?:\/\/[^\s'"]+|(^|[\s'"(])(\/[^\s'"]*)/gi;

const URL_QUERY_OR_FRAGMENT_START = /[?#]/;

/**
 * Builds the `beforeSend` callback passed to `Sentry.init()`.
 *
 * Runs three steps in sequence — log filtering, URL scrubbing, then PII filtering.
 * If log filtering drops the event (returns `null`), the other two are skipped.
 *
 * - **Log filtering** is always active regardless of environment. It prevents
 *   empty/noise events and known browser extension errors from reaching Sentry.
 * - **URL scrubbing** ({@link stripUrlQueries}) is always active too, because a
 *   URL query or fragment can carry a user-typed term.
 * - **PII filtering** is active only when `enablePiiFiltering` is `true`.
 *   It should be disabled for `development` so developers can see full payloads,
 *   and enabled for all other environments.
 *
 * @param enablePiiFiltering - When `true`, PII fields are scrubbed from the event
 * @returns A `beforeSend` function compatible with `Sentry.init()`
 */
export function buildBeforeSend(
  enablePiiFiltering: boolean,
): (event: ErrorEvent, hint: EventHint) => ErrorEvent | null {
  const logFilter = createLogFilterBeforeSend();
  const piiFilter = enablePiiFiltering ? createPiiFilterBeforeSend() : null;

  return (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
    const afterLogFilter = logFilter(event, hint);
    if (afterLogFilter === null) return null;

    const scrubbed = stripUrlQueries(afterLogFilter);
    return piiFilter ? piiFilter(scrubbed, hint) : scrubbed;
  };
}

/**
 * Builds the `beforeSendLog` callback passed to `Sentry.init()`.
 *
 * @returns A function that scrubs URL queries and fragments from a log
 */
export function buildBeforeSendLog(): (log: Log) => Log {
  return (log) => stripUrlQueries(log);
}

/**
 * Builds the `beforeSendTransaction` callback passed to `Sentry.init()`.
 *
 * @returns A function that scrubs URL queries and fragments from a transaction
 */
export function buildBeforeSendTransaction(): (event: TransactionEvent) => TransactionEvent {
  return (event) => stripUrlQueries(event);
}

/**
 * Creates a log-filtering `beforeSend` hook that drops low-value events.
 *
 * Drops the event (returns `null`) in two cases:
 * 1. The event has neither an exception nor a message — these are empty events
 *    that provide no actionable information.
 * 2. The original exception originates from a browser extension — extension
 *    scripts run in the page context and can throw errors that are unrelated to
 *    the application (e.g. `"Script error."`, stack frames from `chrome-extension://`).
 *
 * @returns A `beforeSend`-compatible function
 */
export function createLogFilterBeforeSend() {
  return (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
    if (!event.exception?.values?.length && !event.message) {
      return null;
    }

    if (isKnownBrowserExtensionError(hint.originalException)) {
      return null;
    }

    return event;
  };
}

/**
 * Creates a PII-scrubbing `beforeSend` hook that removes personal data from
 * error events before they leave the browser.
 *
 * Scrubs the following fields:
 * - `event.user.email`, `event.user.ip_address`, `event.user.username`
 * - `event.request.headers` entries whose name matches `PII_HEADER_NAMES`
 *   (`Authorization`, `Cookie`, `X-Forwarded-For`, `X-Real-IP`)
 * - `event.request.cookies` — the full cookie string
 * - `event.request.env` — may contain server-side IP or hostname
 *
 * Note: `sendDefaultPii` is set to `false` in `Sentry.init()` so the SDK
 * itself never attaches PII automatically. This hook provides an additional
 * safety net for PII that enters the event through other means (e.g. set
 * explicitly via `Sentry.setUser()`).
 *
 * @returns A `beforeSend`-compatible function
 */
export function createPiiFilterBeforeSend() {
  // `hint` is intentionally unused: PII scrubbing reads only the event. The parameter is kept so the
  // returned function matches the `beforeSend(event, hint)` contract and its sibling filter.
  return (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
    if (event.user) {
      const sanitisedUser = { ...event.user };
      for (const field of PII_USER_FIELDS) {
        delete sanitisedUser[field];
      }
      event.user = sanitisedUser;
    }

    if (event.request) {
      if (event.request.headers) {
        const sanitisedHeaders: Record<string, string> = {};
        for (const [key, value] of Object.entries(event.request.headers)) {
          if (!PII_HEADER_NAMES.has(key.toLowerCase())) {
            sanitisedHeaders[key] = value;
          }
        }
        event.request.headers = sanitisedHeaders;
      }

      delete event.request.cookies;
      delete event.request.env;
    }

    return event;
  };
}

/**
 * Removes URL queries and fragments from every string of an event, transaction or log, in place.
 *
 * Walks every nested object and array (keys are kept, cycles are skipped) and rewrites only
 * URL- or query-shaped strings:
 * - a string under a key ending in `fragment` is emptied;
 * - a bare query (`searchTerm=x`, `?a=1&b=2`) is emptied;
 * - in any other string, each absolute URL and each root-relative path is cut at its first
 *   `?` or `#`, so free text such as an exception message loses the query too.
 *
 * Runs whatever `enablePiiFiltering` says, because a query can carry a user-typed term.
 *
 * @param value - The event, transaction or log to scrub
 * @returns The same object, scrubbed
 */
export function stripUrlQueries<T>(value: T): T {
  scrubNode(value, new WeakSet());
  return value;
}

/**
 * Cuts a URL or path at its first `?` or `#`.
 *
 * @param urlToken - An absolute URL or a root-relative path
 * @returns The token without its query and fragment
 */
function cutAtQueryOrFragment(urlToken: string): string {
  const cutIndex = urlToken.search(URL_QUERY_OR_FRAGMENT_START);
  return cutIndex === -1 ? urlToken : urlToken.slice(0, cutIndex);
}

/**
 * Returns `true` when the error originates from a browser extension rather
 * than the application itself.
 *
 * Extension scripts run in the host page's JavaScript context and can throw
 * errors that propagate to the application's error boundary. These errors are
 * not caused by the application, so they are filtered out to keep Sentry clean.
 *
 * Detected patterns:
 * - `"Script error."` — the browser hides cross-origin error details; this
 *   generic message almost exclusively comes from extension scripts.
 * - Stack frames containing `extensions/` — common in Chromium-based browsers.
 * - Stack frames starting with `chrome-extension://` or `moz-extension://`.
 *
 * @param error - The original exception from `EventHint.originalException`
 * @returns `true` if the error should be dropped
 */
function isKnownBrowserExtensionError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const msg = error.message ?? '';
  const stack = error.stack ?? '';

  const extensionPatterns = [/^Script error\.?$/i, /extensions\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i];

  return extensionPatterns.some((pattern) => pattern.test(msg) || pattern.test(stack));
}

/**
 * Scrubs the string values of an object or array in place, recursing into nested ones.
 *
 * @param node - The value to walk; anything that is not an object is ignored
 * @param visited - Objects already walked, so a cycle is skipped
 */
function scrubNode(node: unknown, visited: WeakSet<object>): void {
  if (typeof node !== 'object' || node === null || visited.has(node)) return;
  visited.add(node);

  const record = node as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const child = record[key];
    if (typeof child === 'string') {
      record[key] = scrubString(child, key.toLowerCase().endsWith('fragment'));
    } else {
      scrubNode(child, visited);
    }
  }
}

/**
 * Scrubs one string value.
 *
 * @param text - The string to scrub
 * @param isFragmentKey - Whether the string sits under a key that names a URL fragment
 * @returns An empty string for a fragment or a bare query, otherwise the text with each URL cut
 */
function scrubString(text: string, isFragmentKey: boolean): string {
  if (isFragmentKey || BARE_QUERY.test(text)) return '';

  return text.replaceAll(URL_TOKEN, (token: string, prefix: string | undefined, path: string | undefined) =>
    path === undefined ? cutAtQueryOrFragment(token) : prefix + cutAtQueryOrFragment(path),
  );
}
